import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { FlaskConical, Pencil, Trash2, ClipboardCheck, X, FileText, FileDown, ChevronRight, MapPin, Wrench, Users, Link2, Search, PackageCheck } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextInput, NumberInput, TextArea, Select, Button } from "@/components/ui-kit";
import DocViewer from "@/components/DocViewer";
import {
  getMeta, getSheet, listIngredients, listSheetLogs, createSheetLog, deleteSheet, getAvailableInputs,
  downloadSheetPdf, updateSheetLog, deleteSheetLog, searchReceptionLots,
} from "@/lib/api";
import { getUser } from "@/lib/auth";

const fmtLine = (l) =>
  [l.product, l.lot ? `lot ${l.lot}` : null, l.date, l.provider].filter(Boolean).join(" · ");

const lineToConsumed = (group, l) => ({
  source: "reception",
  ref_id: l.reception_id,
  label: `${group.ingredient_name} — ${l.product}${l.provider ? " (" + l.provider + ")" : ""}`,
  lot: l.lot || `albarà ${l.delivery_note || l.date}`,
  quantity: l.quantity ? `${l.quantity}${l.unit ? " " + l.unit : ""}` : "",
});

export default function SheetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = getUser()?.rol === "admin";
  const signer = getUser()?.username || "";
  const { data: meta } = useSWR("meta", getMeta);
  const { data: sheet } = useSWR(["sheet", id], () => getSheet(id));
  const { data: ingredients } = useSWR("ingredients", listIngredients);
  const { data: logs, mutate: mutateLogs } = useSWR(["sheet-logs", id], () => listSheetLogs(id));
  const { data: inputs } = useSWR(["inputs", id], () => getAvailableInputs(id));
  const subproducts = inputs?.subproducts || [];
  const receptionGroups = inputs?.reception || [];

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ date: today, quantity_kg: "", lot: "", notes: "" });
  const [consumed, setConsumed] = useState({}); // ingredient_id -> lot
  // matèria primera de recepció: ingredient_id -> línia d'albarà seleccionada (o null)
  const [receptionSel, setReceptionSel] = useState({});
  const [searchFor, setSearchFor] = useState(null); // grup en cerca manual
  const [saving, setSaving] = useState(false);
  // suggerir per defecte la línia més recent de cada matèria primera
  useEffect(() => {
    if (!receptionGroups.length) return;
    setReceptionSel((prev) => {
      const next = { ...prev };
      receptionGroups.forEach((g) => {
        if (!(g.ingredient_id in next)) next[g.ingredient_id] = g.lines[0] || null;
      });
      return next;
    });
  }, [receptionGroups]);
  // edició d'elaboracions (admin)
  const [editingLog, setEditingLog] = useState(null);
  const [editForm, setEditForm] = useState({ date: "", quantity_kg: "", lot: "", notes: "" });
  const [lightbox, setLightbox] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const [ftIngredient, setFtIngredient] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  const openLightbox = (url) => { setZoomed(false); setLightbox(url); };
  const closeLightbox = () => { setZoomed(false); setLightbox(null); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const allergensMeta = meta?.allergens || [];
  const ingById = Object.fromEntries((ingredients || []).map((i) => [i.id, i]));
  const allergenName = (aid) => allergensMeta.find((a) => a.id === aid)?.name || aid;

  const submit = async () => {
    if (!form.lot.trim() || isNaN(parseFloat(form.quantity_kg)))
      return toast.error("Indica quantitat (kg) i lot");
    setSaving(true);
    try {
      const consumedArr = subproducts
        .filter((g) => consumed[g.ingredient_id])
        .map((g) => ({ source: "production", ref_id: g.base_sheet_id, label: g.base_sheet_name, lot: consumed[g.ingredient_id], quantity: "" }));
      receptionGroups.forEach((g) => {
        const l = receptionSel[g.ingredient_id];
        if (l) consumedArr.push(lineToConsumed(g, l));
      });
      await createSheetLog(id, {
        date: form.date, quantity_kg: parseFloat(form.quantity_kg),
        lot: form.lot.trim(), notes: form.notes, consumed: consumedArr, signer,
      });
      toast.success("Elaboració registrada");
      setForm({ date: today, quantity_kg: "", lot: "", notes: "" });
      setConsumed({});
      setReceptionSel(Object.fromEntries(receptionGroups.map((g) => [g.ingredient_id, g.lines[0] || null])));
      mutateLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error registrant l'elaboració");
    } finally {
      setSaving(false);
    }
  };

  const removeSheet = async () => {
    if (!window.confirm(`Eliminar la fitxa "${sheet?.name}" i el seu històric?`)) return;
    try {
      await deleteSheet(id);
      toast.success("Fitxa eliminada");
      navigate("/production");
    } catch { toast.error("Error eliminant"); }
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadSheetPdf(id, sheet?.name); }
    catch { toast.error("Error generant el PDF"); }
    finally { setPdfBusy(false); }
  };

  const canDeleteLog = (log) =>
    isAdmin || (log.signer === signer && String(log.created_at || "").slice(0, 10) === today);

  const removeLog = async (log) => {
    const msg = isAdmin
      ? `Esborrar l'elaboració del ${log.date} (lot ${log.lot})?`
      : "Esborrar aquesta elaboració per tornar-la a apuntar?";
    if (!window.confirm(msg)) return;
    try {
      await deleteSheetLog(id, log.id);
      toast.success("Elaboració esborrada" + (isAdmin ? "" : " — pots tornar-la a apuntar"));
      mutateLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Error esborrant"); }
  };

  const startEditLog = (log) => {
    setEditingLog(log.id);
    setEditForm({ date: log.date, quantity_kg: String(log.quantity_kg), lot: log.lot, notes: log.notes || "" });
  };

  const saveEditLog = async (log) => {
    if (!editForm.lot.trim() || isNaN(parseFloat(editForm.quantity_kg)))
      return toast.error("Indica quantitat (kg) i lot");
    try {
      await updateSheetLog(id, log.id, {
        date: editForm.date, quantity_kg: parseFloat(editForm.quantity_kg),
        lot: editForm.lot.trim(), notes: editForm.notes,
      });
      toast.success("Elaboració corregida");
      setEditingLog(null);
      mutateLogs();
    } catch (err) { toast.error(err.response?.data?.detail || "Error desant els canvis"); }
  };

  if (!sheet) {
    return (
      <>
        <Header title="Fitxa" />
        <div className="mx-auto max-w-2xl px-4 pt-10 sm:px-6"><Card className="text-center text-sm text-ink-soft">Carregant…</Card></div>
      </>
    );
  }

  return (
    <>
      <Header title="Fitxa" />
      <div className="mx-auto max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {sheet.photos?.length > 0 ? (
              <button onClick={() => openLightbox(sheet.photos[0])} className="shrink-0" aria-label="Ampliar foto">
                <img src={sheet.photos[0]} alt={sheet.name} className="h-16 w-16 rounded-2xl border border-line object-cover sm:h-20 sm:w-20" />
              </button>
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-terracotta text-white sm:h-20 sm:w-20"><FlaskConical size={26} /></div>
            )}
            <div>
              <div className="label-eyebrow">Fitxa de producció</div>
              <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">{sheet.name}</h2>
              {sheet.legal_name && <div className="mt-0.5 text-xs text-ink-soft">Denominació legal: {sheet.legal_name}</div>}
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            <button onClick={downloadPdf} disabled={pdfBusy} title="Descarregar PDF (A4)"
              className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-muted hover:text-terracotta disabled:opacity-50" aria-label="Descarregar PDF">
              <FileDown size={18} />
            </button>
            {isAdmin && (
              <>
                <button onClick={() => navigate(`/production/${id}/edit`)} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-muted hover:text-ink" aria-label="Editar"><Pencil size={18} /></button>
                <button onClick={removeSheet} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-soft hover:bg-ember-bg hover:text-ember" aria-label="Eliminar"><Trash2 size={18} /></button>
              </>
            )}
          </div>
        </div>

        {/* fotos addicionals */}
        {sheet.photos?.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {sheet.photos.slice(1).map((u) => (
              <button key={u} onClick={() => openLightbox(u)} aria-label="Ampliar foto">
                <img src={u} alt="" className="h-16 w-16 rounded-xl border border-line object-cover" />
              </button>
            ))}
          </div>
        )}

        {sheet.derived_allergens?.length > 0 && (
          <div className="mt-5 rounded-2xl bg-ember-bg p-4">
            <div className="label-eyebrow mb-2 text-ember">Al·lèrgens</div>
            <div className="flex flex-wrap gap-1.5">
              {sheet.derived_allergens.map((aid) => (
                <span key={aid} className="rounded-full bg-ember px-2.5 py-1 text-xs font-semibold text-white">{allergenName(aid)}</span>
              ))}
            </div>
          </div>
        )}

        <Card className="mt-4">
          <div className="label-eyebrow mb-3">Ingredients</div>
          {sheet.ingredients?.length === 0 && <div className="text-sm text-ink-soft">Sense ingredients.</div>}
          <div className="space-y-1">
            {sheet.ingredients?.map((it, i) => {
              const ing = ingById[it.ingredient_id];
              const hasFt = ing && (ing.ft_details || ing.datasheets?.length > 0 || ing.allergens?.length > 0);
              return (
                <button
                  key={i}
                  onClick={() => ing && setFtIngredient(ing)}
                  data-testid={`sheet-ing-row-${i}`}
                  className="flex w-full items-start justify-between gap-2 border-b border-line py-2 text-left transition-colors last:border-0 hover:bg-muted/60"
                >
                  <div className="min-w-0">
                    <span className="text-ink">{ing?.name || "—"}</span>
                    {hasFt && <FileText size={12} className="ml-1 inline-block align-middle text-terracotta" />}
                    {it.provider && <div className="text-xs text-ink-soft/70">{it.provider}</div>}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <span className="font-mono text-sm text-ink-soft">{it.quantity}</span>
                    <ChevronRight size={14} className="text-ink-soft/50" />
                  </div>
                </button>
              );
            })}
          </div>
          {sheet.conservation && (
            <div className="mt-3 rounded-xl bg-muted px-3 py-2 text-xs text-ink-soft"><span className="font-semibold">Conservació:</span> {sheet.conservation}</div>
          )}
          {sheet.traces && (
            <div className="mt-2 text-xs text-ink-soft/80"><span className="font-semibold">Trazas:</span> {sheet.traces}</div>
          )}
        </Card>

        {(sheet.elaboration_zone || sheet.equipment || sheet.responsibles) && (
          <Card className="mt-4">
            <div className="label-eyebrow mb-3">Elaboració</div>
            <div className="space-y-3 text-sm">
              {sheet.elaboration_zone && (
                <div className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-terracotta" /><div><div className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Zona</div><div className="text-ink">{sheet.elaboration_zone}</div></div></div>
              )}
              {sheet.equipment && (
                <div className="flex items-start gap-3"><Wrench size={16} className="mt-0.5 shrink-0 text-terracotta" /><div><div className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Maquinària / estris</div><div className="text-ink">{sheet.equipment}</div></div></div>
              )}
              {sheet.responsibles && (
                <div className="flex items-start gap-3"><Users size={16} className="mt-0.5 shrink-0 text-terracotta" /><div><div className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Responsables</div><div className="text-ink">{sheet.responsibles}</div></div></div>
              )}
            </div>
          </Card>
        )}

        {sheet.steps?.length > 0 && (
          <Card className="mt-4">
            <div className="label-eyebrow mb-3">Passos d'elaboració</div>
            <ol className="space-y-2">
              {sheet.steps.map((st, i) => (
                <li key={i} className="flex gap-3 text-sm text-ink"><span className="font-display font-bold text-terracotta">{i + 1}.</span><span>{st}</span></li>
              ))}
            </ol>
          </Card>
        )}

        {/* Registrar elaboració */}
        <Card className="mt-4">
          <div className="mb-3 flex items-center gap-2">
            <ClipboardCheck size={18} className="text-terracotta" />
            <div className="font-display text-lg font-bold text-ink">Registrar elaboració</div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-eyebrow mb-2">Data</div>
                <input data-testid="log-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
                  className="h-14 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" />
              </div>
              <div>
                <div className="label-eyebrow mb-2">Quantitat (kg)</div>
                <NumberInput testid="log-qty" value={form.quantity_kg} onChange={(e) => set("quantity_kg", e.target.value)} placeholder="0.0" />
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">Lot</div>
              <TextInput testid="log-lot" value={form.lot} onChange={(e) => set("lot", e.target.value)} placeholder="Ex: L-20260626" />
            </div>
            <div>
              <div className="label-eyebrow mb-2">Observacions (opcional)</div>
              <TextArea testid="log-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes de l'elaboració" />
            </div>
            {receptionGroups.length > 0 && (
              <div className="rounded-xl border border-forest/30 bg-forest-bg/50 p-3">
                <div className="label-eyebrow mb-2 flex items-center gap-1.5 text-forest"><PackageCheck size={13} /> Matèria primera (albarà d'origen)</div>
                <div className="space-y-3">
                  {receptionGroups.map((g) => {
                    const sel = receptionSel[g.ingredient_id];
                    const selIdx = sel ? g.lines.findIndex((l) =>
                      l.reception_id === sel.reception_id && l.product === sel.product && l.lot === sel.lot) : -1;
                    const isExternal = sel && selIdx === -1; // triada via cerca manual
                    return (
                      <div key={g.ingredient_id}>
                        <div className="mb-1 text-xs font-semibold text-ink">{g.ingredient_name}</div>
                        {isExternal ? (
                          <div className="flex items-center justify-between gap-2 rounded-xl border border-forest/40 bg-white px-3 py-2 text-sm text-ink">
                            <span className="min-w-0 truncate">{fmtLine(sel)}</span>
                            <button onClick={() => setReceptionSel((c) => ({ ...c, [g.ingredient_id]: g.lines[0] || null }))}
                              className="shrink-0 text-xs font-semibold text-ink-soft underline">canviar</button>
                          </div>
                        ) : (
                          <Select value={String(selIdx)} onChange={(e) => {
                            const v = e.target.value;
                            setReceptionSel((c) => ({ ...c, [g.ingredient_id]: v === "-1" ? null : g.lines[Number(v)] }));
                          }}>
                            <option value="-1">— Sense especificar —</option>
                            {g.lines.map((l, li) => (
                              <option key={li} value={String(li)}>
                                {fmtLine(l)}{li === 0 ? "  (més recent)" : ""}
                              </option>
                            ))}
                          </Select>
                        )}
                        <div className="mt-1 flex items-center justify-between">
                          {g.lines.length === 0 && !sel ? (
                            <span className="text-xs text-ink-soft">Cap recepció recent d'aquest producte.</span>
                          ) : <span />}
                          <button onClick={() => setSearchFor(g)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-forest underline">
                            <Search size={12} /> No és aquest? Cerca l'albarà
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {subproducts.length > 0 && (
              <div className="rounded-xl border border-terracotta/30 bg-terracotta/5 p-3">
                <div className="label-eyebrow mb-2 flex items-center gap-1.5 text-terracotta"><Link2 size={13} /> Lot de subproducte consumit</div>
                <div className="space-y-3">
                  {subproducts.map((g) => (
                    <div key={g.ingredient_id}>
                      <div className="mb-1 text-xs font-semibold text-ink">{g.base_sheet_name}</div>
                      <Select value={consumed[g.ingredient_id] || ""} onChange={(e) => setConsumed((c) => ({ ...c, [g.ingredient_id]: e.target.value }))}>
                        <option value="">— Sense especificar —</option>
                        {g.lots.map((l) => (
                          <option key={l.lot + l.date} value={l.lot}>{l.lot} · {l.date} · {l.quantity_kg} kg</option>
                        ))}
                      </Select>
                      {g.lots.length === 0 && <div className="mt-1 text-xs text-ink-soft">Encara no hi ha lots elaborats d'aquesta base.</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={submit} disabled={saving} testid="log-save" className="w-full">{saving ? "Desant…" : "Apuntar elaboració"}</Button>
          </div>
        </Card>

        {/* Històric d'elaboracions */}
        <div className="mt-8">
          <div className="label-eyebrow mb-3">Elaboracions ({logs?.length ?? 0})</div>
          <div className="space-y-2">
            {logs?.length === 0 && <Card className="text-center text-sm text-ink-soft">Encara no s'ha elaborat aquesta fitxa.</Card>}
            {logs?.map((log) => {
              const dateLabel = new Date(log.date).toLocaleDateString("ca-ES", { day: "2-digit", month: "short", year: "numeric" });
              const isEditing = editingLog === log.id;
              return (
                <Card key={log.id} testid={`log-${log.id}`}>
                  {isEditing ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="label-eyebrow mb-1.5">Data</div>
                          <input type="date" value={editForm.date} onChange={(e) => setEditForm((p) => ({ ...p, date: e.target.value }))}
                            className="h-12 w-full rounded-xl border border-line bg-white px-3 text-sm text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" />
                        </div>
                        <div>
                          <div className="label-eyebrow mb-1.5">Quantitat (kg)</div>
                          <NumberInput value={editForm.quantity_kg} onChange={(e) => setEditForm((p) => ({ ...p, quantity_kg: e.target.value }))} className="!h-12 text-sm" />
                        </div>
                      </div>
                      <div>
                        <div className="label-eyebrow mb-1.5">Lot</div>
                        <TextInput value={editForm.lot} onChange={(e) => setEditForm((p) => ({ ...p, lot: e.target.value }))} className="!h-12 text-sm" />
                      </div>
                      <div>
                        <div className="label-eyebrow mb-1.5">Observacions</div>
                        <TextArea rows={2} value={editForm.notes} onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))} className="text-sm" />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => saveEditLog(log)} className="!h-11 flex-1 text-sm">Desar canvis</Button>
                        <Button variant="ghost" onClick={() => setEditingLog(null)} className="!h-11 text-sm">Cancel·lar</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="font-display text-base font-bold text-ink">{dateLabel}</div>
                        <div className="flex items-center gap-2">
                          <div className="font-mono text-sm font-semibold text-terracotta">{log.quantity_kg} kg</div>
                          {isAdmin && (
                            <button onClick={() => startEditLog(log)} aria-label="Corregir elaboració" title="Corregir (quantitat, lot, data…)"
                              className="flex h-8 w-8 items-center justify-center rounded-full text-ink-soft hover:bg-muted hover:text-ink">
                              <Pencil size={15} />
                            </button>
                          )}
                          {canDeleteLog(log) && (
                            <button onClick={() => removeLog(log)} aria-label="Esborrar elaboració"
                              title={isAdmin ? "Esborrar" : "Esborrar per tornar-la a apuntar (només avui)"}
                              className="flex h-8 w-8 items-center justify-center rounded-full text-ink-soft hover:bg-ember-bg hover:text-ember">
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-ink-soft">Lot <strong className="text-ink">{log.lot}</strong> · Signat per {log.signer}</div>
                      {log.consumed?.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {log.consumed.map((c, ci) => (
                            <span key={ci} className="inline-flex items-center gap-1 rounded-full bg-terracotta/10 px-2 py-0.5 text-[11px] font-semibold text-terracotta">
                              <Link2 size={11} /> {c.label}: {c.lot}
                            </span>
                          ))}
                        </div>
                      )}
                      {log.notes && <div className="mt-1 text-xs text-ink-soft">{log.notes}</div>}
                    </>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {ftIngredient && (
        <div onClick={() => setFtIngredient(null)} className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4" data-testid="ft-modal">
          <div onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-surface p-5 sm:rounded-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-eyebrow">Fitxa tècnica</div>
                <h3 className="font-display text-xl font-bold text-ink">{ftIngredient.name}</h3>
              </div>
              <button onClick={() => setFtIngredient(null)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-soft hover:bg-muted" aria-label="Tancar"><X size={20} /></button>
            </div>
            {ftIngredient.allergens?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {ftIngredient.allergens.map((aid) => <span key={aid} className="rounded-full bg-ember px-2.5 py-1 text-xs font-semibold text-white">{allergenName(aid)}</span>)}
              </div>
            )}
            {ftIngredient.ft_details ? (
              <div className="mt-4 whitespace-pre-line rounded-xl bg-muted p-4 text-sm leading-relaxed text-ink">{ftIngredient.ft_details}</div>
            ) : (
              <div className="mt-4 text-sm text-ink-soft">{ftIngredient.allergens?.length ? "Sense més dades de FT registrades." : "Aquest ingredient no té fitxa tècnica registrada."}</div>
            )}
            {ftIngredient.datasheets?.length > 0 && (
              <div className="mt-4">
                <div className="label-eyebrow mb-2">Documents</div>
                <div className="flex flex-wrap gap-2">
                  {ftIngredient.datasheets.map((url) => {
                    const isImg = /\.(png|jpe?g|webp|gif|heic)$/i.test(url);
                    return (
                      <button key={url} type="button" onClick={() => setDocUrl(url)}
                        className="inline-flex flex-col items-center gap-1 rounded-lg border border-line bg-white p-2 text-xs font-semibold text-terracotta transition-colors hover:border-terracotta"
                        aria-label="Veure fitxa tècnica">
                        {isImg ? <img src={url} alt="" className="h-28 w-24 rounded bg-white object-contain" /> : <FileText size={32} />}
                        <span className="inline-flex items-center gap-1"><FileText size={12} /> Veure FT</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {lightbox && (
        <div onClick={closeLightbox} className="fixed inset-0 z-[60] flex items-center justify-center overflow-auto bg-black/90 p-4" data-testid="lightbox">
          <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="fixed right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white" aria-label="Tancar"><X size={22} /></button>
          <img src={lightbox} alt="" onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
            className={zoomed ? "max-w-none cursor-zoom-out rounded-lg" : "max-h-full max-w-full cursor-zoom-in rounded-2xl object-contain"} />
        </div>
      )}

      {docUrl && <DocViewer url={docUrl} onClose={() => setDocUrl(null)} />}

      {searchFor && (
        <ReceptionSearchModal
          group={searchFor}
          onClose={() => setSearchFor(null)}
          onPick={(line) => {
            setReceptionSel((c) => ({ ...c, [searchFor.ingredient_id]: line }));
            setSearchFor(null);
          }}
        />
      )}
    </>
  );
}

function ReceptionSearchModal({ group, onClose, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const data = await searchReceptionLots(q);
        if (alive) setResults(data.lines || []);
      } catch { if (alive) setResults([]); }
      finally { if (alive) setBusy(false); }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4" data-testid="reception-search-modal">
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-2xl bg-surface p-5 sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-eyebrow">Cercar albarà</div>
            <h3 className="font-display text-xl font-bold text-ink">{group.ingredient_name}</h3>
          </div>
          <button onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink-soft hover:bg-muted" aria-label="Tancar"><X size={20} /></button>
        </div>
        <div className="relative mt-3">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
          <input autoFocus type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Producte, proveïdor, lot o nº d'albarà…"
            className="h-12 w-full rounded-xl border border-line bg-white pl-9 pr-4 text-base text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" />
        </div>
        <div className="mt-3 min-h-[10rem] flex-1 space-y-2 overflow-y-auto">
          {busy && <div className="py-6 text-center text-sm text-ink-soft">Cercant…</div>}
          {!busy && results?.length === 0 && <div className="py-6 text-center text-sm text-ink-soft">Cap línia de recepció trobada.</div>}
          {!busy && results?.map((l, i) => (
            <button key={i} onClick={() => onPick(l)}
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-line bg-white p-3 text-left transition-colors hover:border-forest hover:bg-forest-bg/40">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-ink">{l.product || "—"}</div>
                <div className="mt-0.5 text-xs text-ink-soft">
                  {[l.date, l.provider, l.lot ? `lot ${l.lot}` : null, l.delivery_note ? `alb. ${l.delivery_note}` : null].filter(Boolean).join(" · ")}
                </div>
              </div>
              <ChevronRight size={16} className="shrink-0 text-ink-soft" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
